import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import { Flex } from 'components/base/layout-components';
import PieChart from 'components/PieChart';
import StackedAreaChart from 'components/StackedAreaChart';
import {
  PROJECTS,
  getDeliveryChartSeries
} from '../services/projects.services';
import {
  PROJECTS_VIEW_TYPES,
  DELIVERY_STATES_KEYS
} from '../projects.consts.js';
import DeliverySummary from './DeliverySummary';
import DeliveryLegend from './DeliveryLegend';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';

const DeliveryColumn = ({
  displayType,
  issuesData,
  project = {},
  projectConfig = {},
  chartConfig = {},
  projectsExcludedIssues,
  setProjectsExcludedIssues,
  selectedDeliveryState,
  setSelectedDeliveryState,
  isLastIteration = false,
  isSingleIterationView = false,
  deliveryData = [],
  isScrumProject
}) => {
  const { issue_chart: type, id: projectId, name: projectName } = project;
  const { showDeliveryPie } = projectConfig;
  const { icon, totalText, totalValue, totalSuffix } = deliveryData[0] || {};
  const { getSettingOrDefinitionValue } = useSettingsService();

  const pdtContextForKanbanEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.pdtContextForKanbanEnabled
  });

  const chartDataSorted = size =>
    getDeliveryChartSeries(
      size,
      projectId,
      issuesData,
      type,
      isLastIteration,
      isScrumProject,
      projectsExcludedIssues
    );

  const smallStackedChartData = {
    tooltip: { enabled: false },
    chart: {
      backgroundColor: null
    },
    plotOptions: {
      series: {
        animation: false
      }
    },
    xAxis: {
      visible: false
    },
    yAxis: [
      {
        visible: false
      },
      {
        visible: false
      }
    ],
    series: chartDataSorted('small')
  };

  const stackedChartData = {
    chart: {
      backgroundColor: null,
      margin: PROJECTS.LARGE_CHART_MARGIN
    },
    xAxis: {
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
      tickLength: 0,
      lineWidth: 1,
      labels: {
        enabled: false
      }
    },
    yAxis: [
      {
        tickInterval: 10,
        lineWidth: 0,
        gridLineWidth: 0,
        title: {
          enabled: false
        },
        labels: {
          enabled: true,
          style: PROJECTS.CHART_STYLE
        }
      },
      {
        visible: false
      }
    ],
    series: chartDataSorted('large')
  };

  const issuesPieData = chartConfig
    .issuesPie({ issuesData })
    .map(({ key, value, color }) => {
      return {
        name: key,
        value,
        data: [value],
        color: getColor(color || _.get(DELIVERY_STATES_KEYS, `${[key]}.color`))
      };
    });

  const issuesChartSum = _.round(
    _.sum(issuesPieData.map(({ value }) => value))
  );

  const stackedColumnData = {
    tooltip: { enabled: false },
    chart: {
      margin: PROJECTS.SMALL_CHART_MARGIN,
      backgroundColor: null,
      type: 'column'
    },
    xAxis: {
      visible: false
    },
    yAxis: [
      {
        tickPositioner: () => {
          return [0, issuesChartSum];
        },
        visible: false
      },
      {
        visible: false
      }
    ],
    plotOptions: {
      series: {
        pointWidth: 16,
        animation: false
      }
    },
    series: issuesPieData
  };

  switch (displayType) {
    case PROJECTS_VIEW_TYPES.EXTENDED_CHART_VIEW:
      return (
        <StackedAreaChart
          chartOptionsProp={stackedChartData}
          showLegend={false}
        />
      );
    case PROJECTS_VIEW_TYPES.EXTENDED_PIE_VIEW:
      return (
        <Flex items='flex-start'>
          {showDeliveryPie ? (
            <div>
              <PieChart
                data={issuesPieData}
                dataKey='value'
                pieSize={PROJECTS.LARGE_PIE_SIZE}
                showTooltip={false}
                noStroke={true}
              />
            </div>
          ) : (
            <Flex
              style={{
                width: PROJECTS.LARGE_PIE_SIZE.width,
                height: PROJECTS.LARGE_PIE_SIZE.height
              }}
              items='center'
            >
              <StackedAreaChart
                chartOptionsProp={stackedColumnData}
                showLegend={false}
              />
            </Flex>
          )}
          <DeliveryLegend
            issuesData={issuesData}
            projectName={projectName}
            projectId={projectId}
            projectsExcludedIssues={projectsExcludedIssues}
            setProjectsExcludedIssues={setProjectsExcludedIssues}
            selectedDeliveryState={selectedDeliveryState}
            setSelectedDeliveryState={setSelectedDeliveryState}
            isSingleIterationView={isSingleIterationView}
            pdtContextForKanbanEnabled={pdtContextForKanbanEnabled}
          />
        </Flex>
      );
    case PROJECTS_VIEW_TYPES.ITERATION_VIEW:
      return (
        <Flex items='flex-start'>
          <div>
            <PieChart
              data={issuesPieData}
              dataKey='value'
              pieSize={PROJECTS.LARGE_PIE_SIZE}
              showTooltip={false}
              noStroke={true}
            />
          </div>
          <DeliveryLegend
            issuesData={issuesData}
            isLegendHorizontal={false}
            showTitleEllipsisAfter='10rem'
          />
        </Flex>
      );
    case PROJECTS_VIEW_TYPES.PROJECTS_VIEW:
      return (
        <Flex items='center'>
          <DeliverySummary
            totalValue={totalValue}
            totalSuffix={totalSuffix}
            icon={icon}
            title={totalText}
          />
          <Flex style={PROJECTS.SMALL_CHART_SIZE}>
            <StackedAreaChart
              chartOptionsProp={smallStackedChartData}
              showLegend={false}
            />
          </Flex>
          <DeliveryLegend
            issuesData={issuesData}
            isLegendHorizontal={false}
            displayType={displayType}
            isProjectsView={true}
          />
        </Flex>
      );
    default:
      return '';
  }
};

export default DeliveryColumn;
